import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { Loader } from "./Loader";

const iconButtonVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-md transition-colors ease-out-quint duration-140 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed cursor-pointer",
  {
    variants: {
      variant: {
        subtle: "bg-transparent hover:bg-muted",
        light: "bg-multiva-50 hover:bg-multiva-100 dark:bg-multiva-900/40",
        filled: "bg-multiva-600 text-white hover:bg-multiva-700",
        default: "bg-background border border-border hover:bg-muted",
      },
      /**
       * Chip de acción SEMÁNTICO (fondo tintado + ícono a juego). Reemplaza el patrón
       * repetido `iconColor="{c}-600"` + `className="bg-{c}-50 dark:bg-{c}-900/30"`. En modo
       * oscuro usa un fondo MUY sutil (`{c}-500/15`) y un ícono más CLARO (`{c}-400`) —el
       * `-600` fijo del patrón viejo quedaba mudo sobre el fondo saturado `-900/30`—. El ícono
       * hereda el color por `currentColor`, así que NO se pasa `iconColor` con `tone`.
       */
      tone: {
        none: "",
        info: "bg-info-50 text-info-600 hover:bg-info-100 dark:bg-info-500/15 dark:text-info-400 dark:hover:bg-info-500/25",
        exito: "bg-exito-50 text-exito-600 hover:bg-exito-100 dark:bg-exito-500/15 dark:text-exito-400 dark:hover:bg-exito-500/25",
        peligro: "bg-peligro-50 text-peligro-600 hover:bg-peligro-100 dark:bg-peligro-500/15 dark:text-peligro-400 dark:hover:bg-peligro-500/25",
        neutral: "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700/40 dark:text-slate-300 dark:hover:bg-slate-700/60",
      },
      size: {
        xs: "size-6 [&_svg]:size-3.5",
        sm: "size-7 [&_svg]:size-4",
        md: "size-8 [&_svg]:size-[18px]",
        lg: "size-9 [&_svg]:size-5",
        xl: "size-10 [&_svg]:size-6",
      },
    },
    defaultVariants: { variant: "subtle", size: "sm", tone: "none" },
  }
);

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  loading?: boolean;
  /**
   * Color del ícono cuando `variant` NO trae uno fijo (`subtle`/`default`) ·
   * pasa un token de la paleta (ej. `"peligro-600"`, `"esmeralda-600"`) y se
   * resuelve por `var()` (mismo motivo que en `Text`: valor dinámico, no
   * escaneable por Tailwind). Sin esto el ícono hereda `currentColor`.
   */
  iconColor?: string;
}

/**
 * Reemplazo de `<ActionIcon>` de Mantine · botón cuadrado de solo ícono.
 * A diferencia de Mantine, exige `aria-label` en el tipo (no en runtime)
 * para no repetir el hallazgo de accesibilidad de `COMPOSICION-PANTALLAS.md`
 * (Tooltip da `aria-describedby`, no nombre accesible).
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  IconButtonProps & { "aria-label": string }
>(({ variant, size, tone, loading, iconColor, disabled, className, style, children, ...props }, ref) => (
  <button
    ref={ref}
    type={props.type ?? "button"}
    disabled={disabled || loading}
    className={cn(iconButtonVariants({ variant, size, tone }), className)}
    style={iconColor ? { color: `var(--color-${iconColor})`, ...style } : style}
    {...props}
  >
    {loading ? <Loader size={size ?? "sm"} /> : children}
  </button>
));
IconButton.displayName = "IconButton";
